import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { formatCurrency, formatDate } from '../../lib/i18n';
import {
  Plus,
  Search,
  Filter,
  Wrench,
  User,
  ChevronRight,
  Edit,
  Clock,
  Play,
  Package,
  CheckCircle,
  X,
  Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
  waiting_parts: 'bg-orange-100 text-orange-800 border-orange-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  delivered: 'bg-slate-100 text-slate-800 border-slate-200'
};

const statusLabels = {
  en: {
    pending: 'Pending',
    in_progress: 'In Progress',
    waiting_parts: 'Waiting for Parts/Hold',
    completed: 'Completed',
    delivered: 'Delivered'
  },
  bn: {
    pending: 'অপেক্ষমান',
    in_progress: 'চলমান',
    waiting_parts: 'পার্টসের জন্য অপেক্ষা/হোল্ড',
    completed: 'সম্পন্ন',
    delivered: 'ডেলিভারি হয়েছে'
  }
};

export default function JobsListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, language } = useLanguage();
  const { user, hasRole } = useAuth();
  const { garageSlug } = useParams();
  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';
  const [jobs, setJobs] = useState([]);
  const [statusCounts, setStatusCounts] = useState({ pending: 0, in_progress: 0, waiting_parts: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const dateFromRef = useRef(null);
  const dateToRef = useRef(null);
  const [totalCount, setTotalCount] = useState(0);
  const [todayCount, setTodayCount] = useState(0);
  const isSearching = !!(search || dateFrom || dateTo || (statusFilter !== 'all'));

  const canEdit = hasRole(['owner', 'manager', 'front_desk']);

  const buildQueryParams = useCallback((searchTerm) => {
    const params = new URLSearchParams();
    if (statusFilter !== 'all' && statusFilter !== 'parts_only') params.append('status', statusFilter);
    const s = searchTerm !== undefined ? searchTerm : search;
    if (s) params.append('search', s);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    return params;
  }, [statusFilter, search, dateFrom, dateTo]);

  const fetchJobs = useCallback(async (searchTerm) => {
    try {
      const params = buildQueryParams(searchTerm);
      
      const [response, countRes] = await Promise.all([
        axios.get(`${API}/jobs?${params}`),
        axios.get(`${API}/jobs/count?${params}`)
      ]);
      let results = response.data;
      
      // Client-side filter for parts-only jobs
      if (statusFilter === 'parts_only') {
        results = results.filter(j => 
          !(j.services?.length > 0) && !(j.custom_services?.length > 0) && j.parts_used?.length > 0
        );
      }
      
      setJobs(results);
      setTotalCount(countRes.data.count);
      setTodayCount(countRes.data.today_count);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, dateFrom, dateTo, buildQueryParams]);

  // Fetch status counts (all jobs, no filter)
  const fetchStatusCounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      const allJobs = response.data;
      setStatusCounts({
        pending: allJobs.filter(j => j.status === 'pending').length,
        in_progress: allJobs.filter(j => j.status === 'in_progress').length,
        waiting_parts: allJobs.filter(j => j.status === 'waiting_parts').length,
        completed: allJobs.filter(j => j.status === 'completed').length,
      });
    } catch (error) {
      console.error('Failed to fetch status counts:', error);
    }
  }, []);

  useEffect(() => {
    fetchStatusCounts();
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchJobs();
  }, [statusFilter, dateFrom, dateTo]);

  // Debounced auto-search on typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      fetchJobs(search);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const statusOptions = [
    { value: 'all', label: language === 'en' ? 'All Status' : 'সব স্ট্যাটাস' },
    { value: 'pending', label: statusLabels[language].pending },
    { value: 'in_progress', label: statusLabels[language].in_progress },
    { value: 'waiting_parts', label: language === 'en' ? 'Waiting for Parts/Hold' : 'পার্টসের জন্য অপেক্ষা/হোল্ড' },
    { value: 'completed', label: statusLabels[language].completed },
    { value: 'delivered', label: statusLabels[language].delivered },
    { value: 'parts_only', label: language === 'en' ? 'Parts Only' : 'শুধু পার্টস' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Barlow_Condensed']">{t('jobs_title')}</h1>
            <p className="text-slate-500">
              {isSearching
                ? (language === 'en' ? `${jobs.length} results found` : `${jobs.length}টি ফলাফল পাওয়া গেছে`)
                : (language === 'en' ? `${totalCount} jobs found` : `${totalCount}টি জব পাওয়া গেছে`)}
              {' '}
              <span className="text-xs text-blue-600 font-medium">
                ({language === 'en' ? `${todayCount} today` : `আজ ${todayCount}টি`})
              </span>
            </p>
          </div>
          <div className="flex gap-2">
            <Link to={`${basePath}/jobs/new`} data-testid="new-job-btn">
              <Button className="gap-2 bg-blue-800 hover:bg-blue-900">
                <Plus className="w-4 h-4" />
                {t('create_job')}
              </Button>
            </Link>
          </div>
        </div>

        {/* Status Counts Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { key: 'pending', icon: Clock, bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', ring: 'ring-yellow-400', hover: 'hover:bg-yellow-50' },
            { key: 'in_progress', icon: Play, bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', ring: 'ring-blue-400', hover: 'hover:bg-blue-50' },
            { key: 'waiting_parts', icon: Package, bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', ring: 'ring-orange-400', hover: 'hover:bg-orange-50' },
            { key: 'completed', icon: CheckCircle, bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', ring: 'ring-green-400', hover: 'hover:bg-green-50' },
          ].map(({ key, icon: Icon, bg, border, text, ring, hover }) => {
            const isActive = statusFilter === key;
            return (
              <button
                key={key}
                onClick={() => setStatusFilter(isActive ? 'all' : key)}
                className={`rounded-lg border px-3 py-1.5 transition-all ${
                  isActive 
                    ? `${bg} ${border} ring-2 ring-offset-1 ${ring}` 
                    : `bg-white ${border} ${hover}`
                }`}
                data-testid={`status-count-${key}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <Icon className={`w-3.5 h-3.5 ${text}`} />
                    <span className={`text-xs font-medium ${text}`}>
                      {statusLabels[language][key]}
                    </span>
                  </div>
                  <span className={`text-sm font-bold ${text}`}>{statusCounts[key]}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder={language === 'en' ? 'Search by job #, customer, phone, bike, service, parts...' : 'জব নম্বর, গ্রাহক, ফোন, বাইক, সার্ভিস, পার্টস দিয়ে খুঁজুন...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 pr-9 h-12"
                  data-testid="job-search"
                />
                {search && (
                  <button
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 hover:text-red-600 transition-colors"
                    onClick={() => setSearch('')}
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-auto min-w-[120px] flex-[1.2] sm:flex-none sm:w-[220px] h-12" data-testid="status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className="flex items-center gap-2">
                          {opt.value !== 'all' && (
                            <span className={`inline-block w-2.5 h-2.5 rounded-full ${
                              opt.value === 'pending' ? 'bg-yellow-400' :
                              opt.value === 'in_progress' ? 'bg-blue-400' :
                              opt.value === 'waiting_parts' ? 'bg-orange-400' :
                              opt.value === 'completed' ? 'bg-green-400' :
                              opt.value === 'parts_only' ? 'bg-cyan-400' :
                              'bg-slate-400'
                            }`} />
                          )}
                          {opt.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1 flex-1 min-w-0">
                  <Calendar className="w-4 h-4 text-slate-600 flex-shrink-0" />
                  <div
                    className="relative flex-1 min-w-0 h-10 border rounded-md flex items-center cursor-pointer hover:border-blue-400 transition-colors overflow-hidden"
                    onClick={() => dateFromRef.current?.showPicker?.()}
                  >
                    <input
                      ref={dateFromRef}
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="sr-only"
                    />
                    <span className={`text-[11px] sm:text-sm px-1 sm:px-1.5 truncate ${dateFrom ? 'text-foreground' : 'text-slate-400'}`}>
                      {dateFrom ? new Date(dateFrom + 'T00:00:00').toLocaleDateString('en-GB') : 'dd/mm/yyyy'}
                    </span>
                  </div>
                  <span className="text-slate-300 text-xs flex-shrink-0">—</span>
                  <div
                    className="relative flex-1 min-w-0 h-10 border rounded-md flex items-center cursor-pointer hover:border-blue-400 transition-colors overflow-hidden"
                    onClick={() => dateToRef.current?.showPicker?.()}
                  >
                    <input
                      ref={dateToRef}
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="sr-only"
                    />
                    <span className={`text-[11px] sm:text-sm px-1 sm:px-1.5 truncate ${dateTo ? 'text-foreground' : 'text-slate-400'}`}>
                      {dateTo ? new Date(dateTo + 'T00:00:00').toLocaleDateString('en-GB') : 'dd/mm/yyyy'}
                    </span>
                  </div>
                  {(dateFrom || dateTo) && (
                    <button
                      className="text-red-400 hover:text-red-600 transition-colors"
                      onClick={() => { setDateFrom(''); setDateTo(''); }}
                      aria-label="Clear date filter"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Jobs List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : jobs.length > 0 ? (
          <div className="space-y-3">
            {(() => {
              // Group jobs by date labels
              const getDateGroup = (dateStr) => {
                const date = new Date(dateStr);
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
                const weekAgo = new Date(today); weekAgo.setDate(today.getDate() - 7);
                const monthAgo = new Date(today); monthAgo.setMonth(today.getMonth() - 1);
                
                if (date >= today) return language === 'en' ? 'Today' : 'আজ';
                if (date >= yesterday) return language === 'en' ? 'Yesterday' : 'গতকাল';
                if (date >= weekAgo) return language === 'en' ? 'This Week' : 'এই সপ্তাহ';
                if (date >= monthAgo) return language === 'en' ? 'This Month' : 'এই মাস';
                return language === 'en' ? 'Older' : 'পুরনো';
              };

              let lastGroup = null;
              return jobs.map((job) => {
                const group = getDateGroup(job.created_at);
                const showHeader = group !== lastGroup;
                lastGroup = group;
                const isEditable = canEdit && job.status !== 'delivered';

                return (
                  <React.Fragment key={job.id}>
                    {showHeader && (
                      <div className="flex items-center gap-3 pt-4 first:pt-0">
                        <div className="h-px flex-1 bg-slate-200" />
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{group}</span>
                        <div className="h-px flex-1 bg-slate-200" />
                      </div>
                    )}
                    <Card className="hover:border-blue-500 transition-colors cursor-pointer" data-testid={`job-card-${job.id}`}>
                      <CardContent className="p-4 md:p-5">
                        <div className="flex items-start justify-between gap-4">
                          <Link 
                            to={`${basePath}/jobs/${job.id}`}
                            className="flex-1 min-w-0"
                          >
                            {/* Job Header */}
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <span className="font-bold text-blue-800 text-lg">{job.job_number}</span>
                              <Badge className={`${statusColors[job.status]} border`}>
                                {statusLabels[language][job.status]}
                              </Badge>
                              {job.customer_name === 'Quick Entry' && (
                                <Badge className="bg-purple-100 text-purple-800 border-purple-200 border">
                                  {language === 'en' ? 'Quick Entry' : 'কুইক এন্ট্রি'}
                                </Badge>
                              )}
                              {!(job.services?.length > 0) && !(job.custom_services?.length > 0) && job.parts_used?.length > 0 && (
                                <Badge className="bg-cyan-100 text-cyan-800 border-cyan-200 border">
                                  {language === 'en' ? 'Parts Only' : 'শুধু পার্টস'}
                                </Badge>
                              )}
                            </div>

                            {/* Customer & Bike Info */}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-slate-600">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                <span className="font-medium">{job.customer_name}</span>
                                <span className="text-slate-400">•</span>
                                <span>{job.customer_phone}</span>
                              </div>
                              {(job.bike_make || job.bike_model) && (
                                <div className="flex items-center gap-2">
                                  <Wrench className="w-4 h-4" />
                                  <span>{job.bike_make} {job.bike_model}</span>
                                  {job.bike_registration && (
                                    <span className="text-slate-400">({job.bike_registration})</span>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Services */}
                            {job.services?.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {job.services.slice(0, 3).map((s, i) => (
                                  <Badge key={i} variant="secondary" className="text-xs">
                                    {s.name}
                                  </Badge>
                                ))}
                                {job.services.length > 3 && (
                                  <Badge variant="secondary" className="text-xs">
                                    +{job.services.length - 3}
                                  </Badge>
                                )}
                              </div>
                            )}

                            {/* Date */}
                            <p className="text-xs text-slate-400 mt-2">
                              {formatDate(job.created_at, language)}
                            </p>
                          </Link>

                          {/* Amount & Actions */}
                          <div className="text-right flex-shrink-0">
                            <p className="text-xl font-bold text-slate-900">
                              {formatCurrency(job.total_amount, language)}
                            </p>
                            {job.technician_name && (
                              <p className="text-xs text-slate-500 mt-1">
                                {language === 'en' ? 'Tech:' : 'টেক:'} {job.technician_name}
                              </p>
                            )}
                            <div className="flex items-center gap-1 justify-end mt-2">
                              {isEditable && (
                                <Link 
                                  to={`${basePath}/jobs/${job.id}/edit`}
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`edit-job-${job.id}`}
                                >
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-blue-600">
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                </Link>
                              )}
                              <Link to={`${basePath}/jobs/${job.id}`}>
                                <ChevronRight className="w-5 h-5 text-slate-400" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </React.Fragment>
                );
              });
            })()}
          </div>
        ) : (
          <Card>
            <CardContent className="py-20 text-center">
              <Wrench className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 text-lg mb-4">
                {language === 'en' ? 'No jobs found' : 'কোন জব পাওয়া যায়নি'}
              </p>
              <Link to={`${basePath}/jobs/new`}>
                <Button className="bg-blue-800 hover:bg-blue-900">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('create_job')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
